import Vue from "vue";
import groupMainInfoResource from "./group_main_info_resource";
import GroupResource from "../groups/group_resource";
import {CurrentUserClass} from "shared/CurrentUser";
import {DjangoUrls} from "DjangoUrls";
import InviteUserOptionsPopup from "./popups/InviteUserOptionsPopup";
import {simplePopupFactory} from "shared/simple_popups/simplePopupFactory";
import selectFactory from "shared/select_factory/select_factory";
import EmailRoleCategoryPopup from "../../../vue/common_components/invitations/EmailRoleCategoryPopup";
import GroupInviteResource from "../groups/group_invite_resource";

export default {
    namespaced: true,
    state: {
        initialised: false,
        accessError: false,
        group: {
            slug: '',
            is_private: false,
            is_buddy_group: false,
            name: '',
            id: 0,
            description: '',
            avatar: '',
            members_avatars: [],
            auto_avatar: false,
            uses_default_avatar: false,
            link: '',
            community: null,
            owner: null,
        },
        viewer_state: {
            is_member: false,
            is_admin: false,
            is_join_request_pending: false,
            is_invite_pending: false,
        },
        sharing_state: {
            stories: {
                count: 0,
                has_new: false,
            },
            wiki: {
                count: 0,
                has_new: false,
            },
            media: {
                count: 0,
                has_new: false,
            },
        },
    },
    mutations: {
        updateGroup(state, info = {}) {
            for (let k of Object.keys(info)) {
                state.group[k] = info[k];
            }
        },
        updateViewerState(state, info = {}) {
            for (let k of Object.keys(info)) {
                state.viewer_state[k] = info[k];
            }
        },
        updateSharedData(state, info = {}) {
            for (let k of Object.keys(info)) {
                state.sharing_state[k] = info[k];
            }
        },
        setInitialised(state, accessError = false) {
            state.accessError = accessError;
            state.initialised = true;
        }
    },
    getters: {
        initialised(state, getters, rootState) {
            return state.initialised;
        },
        accessError(state, getters, rootState) {
            return state.accessError;
        },
        group(state, getters, rootState) {
            return state.group;
        },
        viewer_state(state, getters, rootState) {
            const viewerState = Object.assign({}, state.viewer_state);
            viewerState.can_invite = false;
            if (viewerState.is_member && new CurrentUserClass().has_permission('invite-users-to-join-group')) {
                if (viewerState.is_admin) {
                    viewerState.can_invite = true;
                } else {
                    viewerState.can_invite = !getters.group.is_private;
                }
            }
            return viewerState;
        },
        sharing_state(state, getters, rootState) {
            return state.sharing_state;
        },
        slug(state, getters, rootState) {
            return state.group.slug;
        },
    },
    actions: {
        loadInfoAndViewerState({state, commit, getters}) {
            return new Promise((resolve, reject) => {
                const $rootScope = Vue.getAngularModule('$rootScope');
                const $http = Vue.getAngularModule('$http');
                $rootScope.show_dimmer();
                groupMainInfoResource($http).get_info_and_viewer_state(getters.slug)
                    .then(resp => {
                        commit('updateGroup', resp.data.group);
                        commit('updateViewerState', resp.data.viewer_state);
                        commit('setInitialised');
                        resolve();
                    }, err => {
                        commit('setInitialised', true);
                        reject();
                    })
                    .finally($rootScope.hide_dimmer);
            });
        },
        loadSharingState({state, commit, getters}) {
            const $http = Vue.getAngularModule('$http');
            GroupResource($http).get_shared_counters(getters.group.id)
                .then(resp => {
                    commit('updateSharedData', resp.data);
                }, () => {
                    Vue.notifications.error('Can\'t load shared items.');
                });
        },
        updateGroupSettings({state, commit, getters}, groupData) {
            const $http = Vue.getAngularModule('$http');
            const $rootScope = Vue.getAngularModule('$rootScope');
            $rootScope.show_dimmer();
            return GroupResource($http).edit_group(getters.group.id, groupData)
                .then(resp => {
                    commit('updateGroup', resp.data);
                }, err => {
                    Vue.notifications.error(err || 'Error');
                })
                .finally($rootScope.hide_dimmer);
        },
        inviteUsers({state, commit, getters}) {
            /* 'existing' || 'link' || 'invite'
            Community group: admin can invite user to register or invite existing or copy link
                             member can invite existed or copy link
            Circle Group:
                            both admin and user could invite existed or copy link
            */

            const $http = Vue.getAngularModule('$http');
            const $rootScope = Vue.getAngularModule('$rootScope');

            const inviteUsersExisted = () => {
                const popupConfig = {
                    header: 'Invite Users',
                    info: 'Select or de-select Users from the list to invite them to this Group',
                    button_yes: 'Invite',
                    button_no: 'Cancel',
                    empty: 'You do not have any users to select',
                };
                if (state.viewer_state.is_admin && !!state.group.community) {
                    popupConfig.additional_filters = [
                        {
                            text: 'Show all public Users',
                            value: false,
                            type: 'switch',
                            filter: true,
                            paramsWhenTrue: {
                                all_public: true
                            }
                        }
                    ];
                }

                selectFactory()
                    .endpoint_selector(state.viewer_state.is_admin ? DjangoUrls["api.v1:group-get-users-for-invite"](state.group.id) : DjangoUrls["api.v1:group-get-users-for-invite-to-view"](state.group.id), true, popupConfig)
                    .then(res => {
                        let new_checked_ids = res[0];
                        let new_unchecked_ids = res[1];
                        $rootScope.show_dimmer();
                        const resource = state.viewer_state.is_admin ? GroupResource($http).invite_users : GroupResource($http).invite_users_to_view; // members can send request to view Group. not direct invite to join. see https://trello.com/c/dYQkpLcB/128-invite-to-group-feature-for-group-members-non-admins-i-want-to-be-able-to-invite-all-users-in-my-circle-to-a-group-page-i-am-a-m
                        return resource(state.group.id, new_checked_ids, new_unchecked_ids);
                    }, () => new Promise(() => {
                    }))
                    .then(resp => {
                        Vue.notifications.success('Invited');
                    }, err => {
                        Vue.notifications.error(err || 'Error');
                    }).finally($rootScope.hide_dimmer);
            };

            const inviteByEmail = function () {
                const {popupComponent, vm} = simplePopupFactory.mount_vue_popup(EmailRoleCategoryPopup);
                popupComponent.show({
                    info: 'Invite somebody by email to join this Group.',
                    header: 'Invite to Register'
                }, ['email'])
                    .then(({email}) => {
                        $rootScope.show_dimmer();
                        return GroupInviteResource($http).invite_by_email(state.group.id, {email});
                    }, err => {
                        return new Promise(() => {
                            vm?.$destroy();
                        });
                    })
                    .then(resp => {
                        Vue.notifications.success('User successfully invited');
                    }, err => {
                        Vue.notifications.error(err || 'Error');
                    })
                    .finally(() => {
                        $rootScope.hide_dimmer();
                        vm?.$destroy();
                    });

            };

            const availableInvitationOptions = ['existing', 'link'];
            if (state.group.community) {
                if (state.viewer_state.is_admin) {
                    availableInvitationOptions.push('invite');
                }
            }
            const {vm, popupComponent, fEl} = simplePopupFactory.mount_vue_popup(InviteUserOptionsPopup);
            popupComponent.show(availableInvitationOptions, state.group.link)
                .then(item => {
                    if (item.selection === 'existing') {
                        inviteUsersExisted();
                    }
                    if (item.selection === 'invite') {
                        inviteByEmail();
                    }
                }, () => {
                })
                .finally(() => {
                    vm?.$destroy();
                    fEl?.focus();
                });
        }
    },
};